import React, { useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useContent } from "../lib/content";
import ProductCard from "../components/ProductCard";
import ArticleCard from "../components/ArticleCard";
import SEO from "../components/SEO";
import {
  ArrowLeft,
  Leaf,
  ShieldCheck,
  Truck,
  Heart,
  MessageCircle,
  Droplet,
  Flower2,
  Grape,
  Sprout,
} from "lucide-react";
import { buildWhatsAppLink, getImageUrl } from "../lib/api";
import ImageWithSkeleton from "../components/ImageWithSkeleton";

const ICON_MAP = {
  Leaf,
  ShieldCheck,
  Truck,
  Heart,
  MessageCircle,
  Droplet,
  Flower2,
  Grape,
  Sprout,
};

function renderIcon(name, className) {
  const I = ICON_MAP[name] || Leaf;
  return <I className={className} />;
}

export default function HomePage() {
  const { products, articles, categories, settings, landing } = useContent();

  const featured = useMemo(
    () => products.filter((p) => p.featured).slice(0, 8),
    [products],
  );
  const latest = useMemo(
    () => articles.filter((a) => a.published !== false).slice(0, 3),
    [articles],
  );

  const hero = landing.hero || {};
  const story = landing.our_story || {};

  // Preload hero background image
  useEffect(() => {
    if (!hero.background_image) return;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = hero.background_image;
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, [hero.background_image]);

  return (
    <>
      <SEO
        title="تحسین | محصولات طبیعی افغانستان · عسل، زعفران هرات، میوه خشک"
        description="فروشگاه تحسین در کابل — عسل خالص، زعفران سرگل هرات و محصولات طبیعی از سراسر افغانستان."
        image={getImageUrl(hero.background_image)}
      />

      {/* HERO — full-width background image with overlay */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0 z-0">
          <ImageWithSkeleton
            src={getImageUrl(hero.background_image)}
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-stone-900/55 via-stone-900/45 to-amber-900/70" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(251,191,36,0.15),transparent_60%)]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-32 lg:pt-32 lg:pb-40 min-h-[620px] flex items-center">
          <div className="max-w-3xl text-right animate-fade-up">
            {hero.badge && (
              <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 text-amber-100 px-5 py-2 rounded-full text-sm font-semibold mb-7">
                <Leaf className="w-4 h-4 text-amber-300" /> {hero.badge}
              </span>
            )}
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white leading-[1.1] mb-6 drop-shadow-lg">
              {hero.title_top}
              <br />
              <span className="relative inline-block text-amber-300">
                {hero.title_highlight}
                <svg
                  className="absolute -bottom-2 right-0 w-full"
                  height="12"
                  viewBox="0 0 300 12"
                  fill="none"
                >
                  <path
                    d="M2 8 C 80 2, 220 2, 298 8"
                    stroke="#FCD34D"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-amber-50/90 max-w-2xl leading-relaxed mb-10 drop-shadow">
              {hero.subtitle}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold px-8 py-4 rounded-full shadow-lg shadow-amber-900/30 transition-all hover:scale-[1.02]"
                data-testid="hero-cta-products"
              >
                {hero.cta_primary} <ArrowLeft className="w-4 h-4" />
              </Link>
              <a
                href={buildWhatsAppLink(
                  settings.whatsapp_number,
                  "سلام، می‌خواهم از محصولات تحسین سفارش بدهم.",
                )}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/30 hover:bg-white/20 text-white font-semibold px-8 py-4 rounded-full transition-all"
                data-testid="hero-cta-whatsapp"
              >
                <MessageCircle className="w-4 h-4" /> {hero.cta_secondary}
              </a>
            </div>

            {landing.trust_badges?.length > 0 && (
              <div className="mt-12 flex flex-wrap gap-x-10 gap-y-4">
                {landing.trust_badges.map((b, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-white/90"
                  >
                    {renderIcon(b.icon, "w-5 h-5 text-amber-300")}
                    <span className="text-sm font-medium">{b.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* decorative bottom wave */}
        <svg
          className="absolute bottom-0 left-0 w-full text-[#faf9f6]"
          viewBox="0 0 1440 80"
          preserveAspectRatio="none"
          aria-hidden
        >
          <path
            d="M0,60 C240,10 480,80 720,50 C960,20 1200,70 1440,40 L1440,80 L0,80 Z"
            fill="currentColor"
          />
        </svg>
      </section>

      {/* CATEGORIES — rich cards with backgrounds */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20"
        data-testid="home-categories"
      >
        {landing.categories_section && (
          <div className="text-center mb-12">
            {landing.categories_section.eyebrow && (
              <span className="text-amber-700 font-semibold text-sm tracking-[0.2em] uppercase">
                {landing.categories_section.eyebrow}
              </span>
            )}
            <h2 className="text-3xl sm:text-5xl font-black text-stone-900 mt-3 mb-3">
              {landing.categories_section.title}
            </h2>
            <p className="text-stone-600 max-w-xl mx-auto">
              {landing.categories_section.subtitle}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {categories.map((c, i) => (
            <Link
              key={c.key}
              to={`/products?category=${c.key}`}
              className="group relative rounded-3xl overflow-hidden aspect-[3/4] product-card-hover animate-fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
              data-testid={`category-card-${c.key}`}
            >
              <ImageWithSkeleton
                src={getImageUrl(c.image)}
                thumb={getImageUrl(c.image, "thumb")}
                alt={c.name_fa}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/40 to-stone-900/10" />
              <div className="relative h-full flex flex-col justify-end p-6 text-right">
                <div className="w-11 h-11 rounded-2xl bg-amber-500/90 backdrop-blur-sm flex items-center justify-center mb-4 self-end">
                  {renderIcon(c.icon, "w-5 h-5 text-white")}
                </div>
                <h3 className="text-2xl font-black text-white mb-1.5 drop-shadow">
                  {c.name_fa}
                </h3>
                <p className="text-sm text-amber-50/80 leading-relaxed line-clamp-2 mb-3">
                  {c.description}
                </p>
                <span className="inline-flex items-center gap-1 text-amber-300 text-sm font-semibold group-hover:translate-x-1 transition-transform">
                  مشاهده قفسه <ArrowLeft className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section
        className="bg-cream-100 py-20"
        data-testid="home-featured-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              {landing.featured_section?.eyebrow && (
                <span className="text-amber-700 font-semibold text-sm tracking-[0.2em] uppercase">
                  {landing.featured_section.eyebrow}
                </span>
              )}
              <h2 className="text-3xl sm:text-4xl font-black text-stone-900 mt-2">
                {landing.featured_section?.title || "محصولات ویژه"}
              </h2>
            </div>
            <Link
              to="/products"
              className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 font-semibold"
              data-testid="home-view-all-products"
            >
              مشاهده همه <ArrowLeft className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* OUR STORY */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
        data-testid="home-about-teaser"
      >
        <div className="relative">
          <div className="absolute -top-6 -right-6 w-32 h-32 bg-amber-200/60 rounded-full blur-3xl" />
          <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-amber-400/30 rounded-full blur-3xl" />
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl shadow-amber-900/20">
          <ImageWithSkeleton
            src={getImageUrl(story.image)}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
        <div className="text-right">
          {story.eyebrow && (
            <span className="text-amber-700 font-semibold text-sm tracking-[0.2em] uppercase">
              {story.eyebrow}
            </span>
          )}
          <h2 className="text-3xl sm:text-5xl font-black text-stone-900 mt-3 mb-6 leading-tight">
            {story.title_top}
            <br />
            <span className="text-amber-700">{story.title_bottom}</span>
          </h2>
          {(story.body || []).map((p, i) => (
            <p
              key={i}
              className={`text-stone-600 leading-relaxed mb-4 ${i === 0 ? "text-lg" : ""}`}
            >
              {p}
            </p>
          ))}
          <Link
            to="/about"
            className="inline-flex items-center gap-2 border-2 border-stone-300 hover:border-amber-600 hover:text-amber-700 text-stone-700 font-semibold px-6 py-3 rounded-full transition-colors mt-3"
            data-testid="home-about-link"
          >
            {story.cta_label || "بیشتر بدانید"}{" "}
            <ArrowLeft className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ARTICLES */}
      <section
        className="bg-stone-50 py-20"
        data-testid="home-articles-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              {landing.articles_section?.eyebrow && (
                <span className="text-amber-700 font-semibold text-sm tracking-[0.2em] uppercase">
                  {landing.articles_section.eyebrow}
                </span>
              )}
              <h2 className="text-3xl sm:text-4xl font-black text-stone-900 mt-2">
                {landing.articles_section?.title || "مقالات"}
              </h2>
            </div>
            <Link
              to="/articles"
              className="inline-flex items-center gap-1 text-amber-700 font-semibold"
              data-testid="home-view-all-articles"
            >
              همه مقالات <ArrowLeft className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {latest.map((a, i) => (
              <ArticleCard key={a.id} article={a} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20"
        data-testid="home-cta-band"
      >
        <div className="relative overflow-hidden rounded-3xl honeycomb-pattern p-10 sm:p-16 text-right">
          <Heart className="absolute -top-6 -left-6 w-40 h-40 text-amber-300/30" />
          <h2 className="text-3xl sm:text-4xl font-black text-stone-900 mb-4">
            {landing.cta_band?.title}
          </h2>
          <p className="text-lg text-stone-700 max-w-2xl mb-7">
            {landing.cta_band?.body}
          </p>
          <a
            href={buildWhatsAppLink(
              settings.whatsapp_number,
              "سلام، درباره محصولات تحسین سوال داشتم.",
            )}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white font-semibold px-7 py-3.5 rounded-full"
            data-testid="home-cta-contact"
          >
            <MessageCircle className="w-5 h-5" /> {landing.cta_band?.cta_label}
          </a>
        </div>
      </section>
    </>
  );
}
